import { warn } from 'helper/debug';
import mapBackendConfig from 'helper/mapBackendConfig';

const ENV = process.env.NODE_ENV || 'production';
let instanceId = 1;

export const supportedTypes = ['localStorage', 'sessionStorage'];
export type storageTypes = 'localStorage' | 'sessionStorage';

export type ClientStorageOptions = {
	limiter?: string;
	postfix?: string;
	prefix?: string;
};

export const StorageTypes = {
	LOCAL: 'localStorage',
	SESSION: 'sessionStorage',
};

export const IO_TRANSACTION = '__CLIENT_STORAGE_IO_TRANSACTION__';

export default class ClientStorage {
	public static serialize(value: Object): string {
		return JSON.stringify(value);
	}

	public static parse(serialized: string): any {
		return JSON.parse(serialized);
	}

	private id: number;
	private type: string;
	private options: ClientStorageOptions & { type: string } = undefined as any;
	private sandbox: Storage = undefined as any;

	constructor(type: string, options: ClientStorageOptions = {} as any) {
		options = options || {};
		this.id = instanceId++;
		this.type = type;

		if (typeof type !== 'string' || supportedTypes.indexOf(type) === -1) {
			throw new TypeError(`Type can be ${supportedTypes.join(', ')} but not ${type}.`);
		}

		this.configure({ ...options, type });
	}

	public configure(
		options: ClientStorageOptions & { type: string } = {
			limiter: '_',
			postfix: '',
			prefix: '',
			type: '',
		}
	) {
		this.options = options;
		this.options.limiter = options.limiter || '_';
		this.options.postfix = options.postfix ? this.options.limiter + options.postfix : mapBackendConfig.env.language;
		this.options.prefix = options.prefix ? options.prefix + this.options.limiter : 'impulscoach_react';

		if (options.type && !this.sandbox) {
			this.sandbox = window[options.type];
		}
	}

	public create(type: string): ClientStorage {
		return new ClientStorage(type);
	}

	get supported(): boolean {
		return typeof this.sandbox !== 'undefined';
	}

	get supportsIOTransaction(): boolean {
		if (!this.supported) {
			return false;
		}

		try {
			this.sandbox.setItem(IO_TRANSACTION, IO_TRANSACTION);
			this.sandbox.removeItem(IO_TRANSACTION);
			return true;
		} catch (err) {
			warn(
				[
					`${this.type}: Browser does not support IO transactions,`,
					`referr to https://caniuse.com/#search=localStorage.`,
				].join(' ')
			);
			return false;
		}
	}

	get size(): number {
		if (typeof Object.keys === 'function') {
			return Object.keys(this.sandbox).length;
		}

		return -1;
	}

	public generateKey(key: string | number): string {
		const opts: ClientStorageOptions = this.options;

		if (typeof key !== 'string') {
			key = `[${key.toString()}]`;
		}

		return `${opts.prefix || ''}${opts.limiter || ''}${key}${opts.limiter || ''}${opts.postfix || ''}`;
	}

	public get(key: string): any {
		if (!this.supported) {
			return false;
		}

		key = this.generateKey(key);
		const raw = this.sandbox.getItem(key);

		if (raw) {
			return ClientStorage.parse(raw);
		}

		return null;
	}

	public set(key: string, value: any): boolean {
		if (!key || !this.supported) {
			return false;
		}

		key = this.generateKey(key);

		try {
			value = ClientStorage.serialize(value);
			this.sandbox.setItem(key, value);

			return true;
		} catch (err) {
			warn(`${this.type}.set: ${err}`);
			return false;
		}
	}

	public delete(key: string): boolean {
		if (!this.supported) {
			return false;
		}

		key = this.generateKey(key);

		try {
			this.sandbox.removeItem(key);
			return true;
		} catch (err) {
			return false;
		}
	}

	public exists(key: string): boolean {
		if (!this.supported) {
			return false;
		}

		key = this.generateKey(key);
		return !!this.get(key);
	}

	public getStore(): boolean | Storage {
		if (!this.supported) {
			return false;
		}

		return this.sandbox;
	}
}

export const SessionStorage = new ClientStorage(StorageTypes.SESSION);
export const LocalStorage = new ClientStorage(StorageTypes.LOCAL);

if (ENV === 'development') {
	(window as any).__STORAGE_ACCESSOR__ = {
		SessionStorage,
		LocalStorage,
	};
}
